import type { RouteRecordRaw } from 'vue-router'

export const appAuthRoutes: Array<RouteRecordRaw> = [
  {
    path: '/app',
    name: 'App',
    meta: { requiresAuth: true },
    // MIL-334 Phase 2 — was 'Projects'; revisit once Projects unlocks (may
    // stay Orders, may become configurable, see the plan's "What Comes After").
    redirect: { name: 'Orders' },
  },
  {
    path: '/app/terms-of-service',
    name: 'TermsOfService',
    meta: { requiresAuth: true, hideShell: true },
    component: () => import('@/views/TermsOfServiceView.vue'),
  },
  {
    path: '/app/account',
    name: 'Account',
    meta: { requiresAuth: true },
    component: () => import('@/views/AccountView.vue'),
  },
  {
    path: '/app/account/profile',
    name: 'AccountProfile',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/ProfileView.vue'),
  },
  {
    path: '/app/account/team',
    name: 'AccountTeam',
    meta: { requiresAuth: true, requiresRole: 'admin' },
    component: () => import('@/views/AccountTeamView.vue'),
  },
  {
    path: '/app/account/settings',
    name: 'AccountSettings',
    meta: { requiresAuth: true },
    component: () => import('@/views/AccountSettingsView.vue'),
  },
  {
    path: '/app/account/stats',
    name: 'AccountStats',
    meta: { requiresAuth: true },
    component: () => import('@/views/AccountStatsView.vue'),
  },
  {
    path: '/app/account/pricing',
    name: 'AccountPricing',
    meta: { requiresAuth: true },
    component: () => import('@/views/PricingView.vue'),
  },
  // MIL-127 — app-lock variant. Settings screen is its own route (not folded
  // into AccountSettingsView) to avoid restructuring the MIL-60 Account views.
  {
    path: '/app/app-lock/settings',
    name: 'AppLockSettings',
    meta: { requiresAuth: true },
    component: () => import('@/views/AppLockSettingsView.vue'),
  },
  {
    path: '/app/app-lock',
    name: 'AppLock',
    meta: { requiresAuth: true, hideShell: true },
    component: () => import('@/views/AppLockView.vue'),
  },
  {
    path: '/app/about',
    name: 'About',
    meta: { requiresAuth: true },
    component: () => import('@/views/AboutView.vue'),
  },
  {
    path: '/app/projects',
    name: 'Projects',
    meta: { requiresAuth: true },
    component: () => import('@/views/ProjectsListView.vue'),
  },
  {
    path: '/app/projects/new',
    name: 'ProjectNew',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/ProjectFormView.vue'),
  },
  {
    path: '/app/projects/:projectId',
    name: 'ProjectDetail',
    meta: { requiresAuth: true },
    component: () => import('@/views/ProjectDetailView.vue'),
  },
  {
    path: '/app/orders',
    name: 'Orders',
    meta: { requiresAuth: true },
    component: () => import('@/views/OrdersListView.vue'),
  },
  // MIL-334 Phase 3/4 — the real "create order without a project" flow. Kept
  // separate from 'Order'/'OrderDetail' below: a draft isn't a placed order
  // yet, so it gets its own page rather than reusing either of those.
  {
    path: '/app/orders/create',
    name: 'CreateOrder',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/CreateOrderView.vue'),
  },
  {
    path: '/app/orders/:orderId/fill-out',
    name: 'FillOutOrder',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/FillOutOrderView.vue'),
  },
  {
    path: '/app/orders/:orderId/fill-out/samples/new',
    name: 'AddDraftSample',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/AddDraftSampleView.vue'),
  },
  // Step 2 of the add-sample flow (analyses, then floor plan/label/photos/info
  // as they're added) — the category chosen on AddDraftSample above is handed
  // off via addSampleStore rather than a route param, same pattern as
  // SelectOrderTatView -> PlaceOrderView's placeOrderStore hand-off.
  {
    path: '/app/orders/:orderId/fill-out/samples/new/details',
    name: 'AddSampleDetails',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/AddSampleDetailsView.vue'),
  },
  // Editing an existing sample skips the category step entirely (the sample
  // already has analyses to derive it from), so this goes straight to the
  // same details page used by the create flow's step 2.
  {
    path: '/app/orders/:orderId/fill-out/samples/:sampleId',
    name: 'EditDraftSample',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/AddSampleDetailsView.vue'),
  },
  // Once an order is placed the customer may still correct a sample's own details — the same details
  // page as the draft edit, in a mode where the sample area and the analyses are locked. Entered from
  // the sample on the order's Prøver tab.
  {
    path: '/app/orders/:orderId/samples/:sampleId/edit',
    name: 'EditPlacedSample',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/AddSampleDetailsView.vue'),
  },
  // "Lab Customer — Place Order" plan — TAT selection (Phase 3), then
  // hand-over method/day selection + final submit (Phase 4+5 combined).
  //
  // flowOrder: the transition-direction guard (router/index.ts) normally
  // infers forward/back purely from path depth, which works everywhere else
  // in this app because every route is genuinely RESTfully nested. This one
  // pair is the exception — PlaceOrder's path is a path-segment *shorter*
  // than SelectOrderTat's even though it's the later step, since "tat" was
  // nested under "place" rather than being a true sibling step. Without
  // flowOrder, stepping from TAT selection to the final hand-over/submit
  // page would animate as if going backward. See router/index.ts's guard
  // for how this is consumed.
  {
    path: '/app/orders/:orderId/place/tat',
    name: 'SelectOrderTat',
    meta: { requiresAuth: true, hideBottomNav: true, flowOrder: 1 },
    component: () => import('@/views/SelectOrderTatView.vue'),
  },
  {
    path: '/app/orders/:orderId/place',
    name: 'PlaceOrder',
    meta: { requiresAuth: true, hideBottomNav: true, flowOrder: 2 },
    component: () => import('@/views/PlaceOrderView.vue'),
  },
  // MIL-417 — the "Næste skridt" page shown after placing an order. flowOrder 3 so the transition
  // from PlaceOrder (flowOrder 2) animates forward: this path is the same depth as /place, so the
  // router's depth-based direction guess alone can't tell which way it moves.
  {
    path: '/app/orders/:orderId/placed',
    name: 'OrderPlaced',
    meta: { requiresAuth: true, hideBottomNav: true, flowOrder: 3 },
    component: () => import('@/views/OrderPlacedView.vue'),
  },
  {
    path: '/app/orders/:orderId',
    name: 'Order',
    redirect: { name: 'OrderResults' },
    meta: { requiresAuth: true },
    component: () => import('@/views/OrderView.vue'),
    children: [
      {
        path: 'results',
        name: 'OrderResults',
        component: () => import('@/views/OrderResultsView.vue'),
      },
      {
        path: 'report',
        name: 'OrderReport',
        component: () => import('@/views/OrderReportView.vue'),
      },
      // The "Aflevering" tab (MIL-417) was replaced by the hand-over line on the order header;
      // keep old deep links working by sending them to the order's first tab.
      {
        path: 'delivery',
        redirect: { name: 'OrderResults' },
      },
    ],
  },
  // "Lab Customer — Projects, Floor Plans & Samples" plan — "Opret ordre" from
  // inside an existing project. Reuses CreateOrderView.vue (it reads
  // route.params.projectId when present to pre-fill address/case-number/
  // insurance and to send projectID on create) rather than a separate view.
  {
    path: '/app/projects/:projectId/orders/new',
    name: 'ProjectCreateOrder',
    meta: { requiresAuth: true, hideBottomNav: true },
    component: () => import('@/views/CreateOrderView.vue'),
  },
]
